import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { BLOCKING_ISSUES, ISSUE_KEYS } from './productImport';

// [one, many] for "1 product needs a name" / "3 products need a name".
const ISSUE_PHRASES = {
  name: ['needs a name', 'need a name'],
  price: ['needs a trade price', 'need a trade price'],
  sellingPrice: ['has a selling price to fix', 'have a selling price to fix'],
  belowTrade: ['sells for less than its trade price', 'sell for less than their trade price'],
  salesCode: ['has no sales code', 'have no sales code'],
  category: ['has no category', 'have no category'],
  vendor: ['has no vendor', 'have no vendor'],
  image: ['has no image', 'have no image'],
};

// Short names for an issue once its rows are being shown on their own.
export const ISSUE_TITLES = {
  name: 'Name required',
  price: 'Trade price required',
  sellingPrice: 'Selling price to fix',
  belowTrade: 'Sells below trade price',
  salesCode: 'No sales code',
  category: 'No category',
  vendor: 'No vendor',
  image: 'No image',
};

// The problems on the rows being imported, listed above the review table with
// the required ones first. "Fix these" narrows the table to a problem's rows
// so the bulk bar below can fill them in at once; "Leave these out" unticks
// rows with a required problem instead.
export default function ImportIssues({
  summary,
  noun,
  nouns,
  activeKey,
  fixableKeys,
  onToggle,
  onLeaveOut,
  onFillTradePrices,
  pendingImageFiles,
  onUploadMatchingImages,
  hasSalesCodes,
}) {
  const issues = ISSUE_KEYS.filter((key) => summary.issues[key] > 0);
  const describe = (key) => {
    const count = summary.issues[key];
    return `${count} ${count === 1 ? noun : nouns} ${ISSUE_PHRASES[key][count === 1 ? 0 : 1]}`;
  };

  return (
    <section className="pi-card pi-issues" aria-labelledby="pi-issues-title">
      <div className="pi-issues-head">
        <h3 id="pi-issues-title">Needs attention</h3>
        <p>Required items block the import. The rest are worth filling in before it.</p>
      </div>

      {summary.selected === 0 ? (
        <p className="pi-issues-clear is-muted">Nothing is selected to import.</p>
      ) : issues.length === 0 ? (
        <p className="pi-issues-clear">
          <CheckCircle2 size={18} aria-hidden="true" /> Everything is filled in. Ready to import.
        </p>
      ) : (
        <ul className="pi-issue-list">
          {issues.map((key) => {
            const blocking = BLOCKING_ISSUES.has(key);
            const isActive = activeKey === key;
            const fixable = fixableKeys.has(key);
            return (
              <li
                key={key}
                className={`pi-issue ${blocking ? 'is-error' : 'is-warning'}${
                  isActive ? ' is-active' : ''
                }`}
              >
                {blocking ? (
                  <AlertCircle size={18} aria-hidden="true" />
                ) : (
                  <AlertTriangle size={18} aria-hidden="true" />
                )}
                <span className="pi-issue-text">{describe(key)}</span>
                {blocking && <span className="pi-chip pi-chip--error">Required</span>}
                <span className="pi-issue-actions">
                  {key === 'price' && summary.fillableTradePrices > 0 && (
                    <button type="button" className="pi-link" onClick={onFillTradePrices}>
                      Use the price before markup for {summary.fillableTradePrices}
                    </button>
                  )}
                  {key === 'image' && pendingImageFiles && (
                    <button type="button" className="pi-link" onClick={onUploadMatchingImages}>
                      Upload matching files
                    </button>
                  )}
                  {key === 'salesCode' && !hasSalesCodes && (
                    <span className="pi-issue-note">This studio has no sales codes set up.</span>
                  )}
                  {blocking && onLeaveOut && (
                    <button
                      type="button"
                      className="pi-link"
                      aria-label={`Leave these out: ${describe(key)}`}
                      onClick={() => onLeaveOut(key)}
                    >
                      Leave these out
                    </button>
                  )}
                  {isActive ? (
                    <button
                      type="button"
                      className="pi-btn pi-btn--outline"
                      onClick={() => onToggle(key)}
                    >
                      Show all
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="pi-btn pi-btn--outline"
                      aria-label={`${fixable ? 'Fix these' : 'Show these'}: ${describe(key)}`}
                      onClick={() => onToggle(key)}
                    >
                      {fixable ? 'Fix these' : 'Show these'}
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
